import { MultiSelect, SingleSelect } from "@appretio/ui-kit";
import React, { useMemo } from "react";
import type {
  TemporalConfig,
  TemporalContextField,
  TemporalContextOption
} from "./temporalConfig";

interface TemporalDropdownProps {
  /**
   * Which context field this dropdown is for
   */
  field: TemporalContextField;
  /**
   * Configuration for this temporal context field
   */
  config: TemporalConfig;
  /**
   * Currently selected options for this temporal context field
   */
  selected?: TemporalContextOption<TemporalContextField>;
  /**
   * Sets selected options for this temporal context field
   */
  setSelected: (
    field: TemporalContextField,
    value: string | string[] | undefined
  ) => void;
  /**
   * Shows the label as the placeholder instead of above the dropdown
   */
  labelAsPlaceholder?: boolean;
  /**
   * Whether the dropdown filters its options as the user types
   */
  typeToSearch?: boolean;
}

export const TemporalContextDropdown = React.memo(
  ({
    field,
    config,
    selected,
    setSelected,
    labelAsPlaceholder = false,
    typeToSearch = true
  }: TemporalDropdownProps) => {
    const { label, options, multi, clearable } = config;
    const labelProps = labelAsPlaceholder ? { placeholder: label } : { label };

    // Memoize options transformation for this specific dropdown
    const opts = useMemo(
      () =>
        options.map((opt) => ({
          label: opt.label,
          value: String(opt.value)
        })),
      [options]
    );

    if (multi) {
      const value = Array.isArray(selected) ? selected.map(String) : [];

      return (
        <MultiSelect
          options={opts}
          {...labelProps}
          value={value}
          onChange={(value) => setSelected(field, value)}
          typeToSearch={typeToSearch}
        />
      );
    }

    const selectedOption = selected
      ? opts.find((opt) => opt.value === String(selected))
      : undefined;

    return (
      <SingleSelect
        options={opts}
        {...labelProps}
        selectedOption={selectedOption}
        onChange={(value) => setSelected(field, value)}
        clearable={clearable}
        typeToSearch={typeToSearch}
      />
    );
  }
);
